import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { propertyService } from '../../../services/propertyService';

const CommuneContext = ({ commune, property }) => {
  const [propertiesInCommune, setPropertiesInCommune] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTour, setActiveTour] = useState(null);

  useEffect(() => {
    const loadCommuneProperties = async () => {
      if (!commune?.id || !property?.id) return;
      try {
        setLoading(true);
        const properties = await propertyService?.getPropertiesInCommune(commune?.id, property?.id, 4);
        setPropertiesInCommune(properties);
      } catch (error) {
        console.error('Error loading commune properties:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCommuneProperties();
  }, [commune?.id, property?.id]);

  if (!commune) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(price);
  };

  const formatNumber = (num) => new Intl.NumberFormat('fr-FR')?.format(num);

  const categoryIcons = {
    schools: { icon: 'GraduationCap', label: 'Écoles' },
    hospitals: { icon: 'Heart', label: 'Hôpitaux' },
    shopping: { icon: 'ShoppingBag', label: 'Shopping' },
    restaurants: { icon: 'Utensils', label: 'Restaurants' },
    transport: { icon: 'Bus', label: 'Transport' },
    banks: { icon: 'Banknote', label: 'Banques' },
    markets: { icon: 'Store', label: 'Marchés' },
    entertainment: { icon: 'Music', label: 'Loisirs' },
    sports: { icon: 'Trophy', label: 'Sports' },
  };

  // Resolve Matterport tours from commune data
  const matterportTours = commune?.matterport_tours || commune?.virtual_tours || [];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 bg-brand-gold/10 rounded-md flex items-center justify-center">
          <Icon name="MapPin" size={18} color="var(--color-brand-gold)" />
        </div>
        <h2 className="text-xl font-heading font-bold text-foreground">
          Découvrir {commune?.name}
        </h2>
      </div>

      {/* Commune Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* About */}
        <div>
          <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
            À propos du quartier
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {commune?.description ||
              `${commune?.name} est un quartier dynamique d'${commune?.district || 'Abidjan'} offrant un cadre de vie agréable avec de nombreuses commodités.`}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {commune?.population && (
              <div className="text-center p-3 bg-muted/40 rounded-md border border-border">
                <p className="font-bold text-base text-brand-earth">{formatNumber(commune?.population)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Habitants</p>
              </div>
            )}
            {commune?.average_price && (
              <div className="text-center p-3 bg-muted/40 rounded-md border border-border">
                <p className="font-bold text-base text-brand-earth">{formatPrice(commune?.average_price)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Prix moyen</p>
              </div>
            )}
          </div>
        </div>

        {/* Amenities */}
        <div>
          <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
            Commodités
          </h3>
          {commune?.amenities && Object.keys(commune?.amenities)?.length > 0 ? (
            <div className="space-y-2.5">
              {Object.entries(commune?.amenities)?.map(([category, items]) => {
                if (!items || (Array.isArray(items) && items?.length === 0)) return null;
                const cat = categoryIcons?.[category] || { icon: 'MapPin', label: category };
                return (
                  <div key={category} className="flex items-start gap-2.5">
                    <Icon name={cat?.icon} size={14} color="var(--color-brand-gold)" className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-foreground mb-0.5">{cat?.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {Array.isArray(items) ? items?.join(', ') : items}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {[
                { icon: 'GraduationCap', label: 'Écoles et universités' },
                { icon: 'ShoppingBag', label: 'Centres commerciaux' },
                { icon: 'Heart', label: 'Centres de santé' },
                { icon: 'Bus', label: 'Transport public' },
                { icon: 'Utensils', label: 'Restaurants et cafés' },
              ]?.map((amenity, index) => (
                <div key={index} className="flex items-center gap-2.5">
                  <Icon name={amenity?.icon} size={14} color="var(--color-brand-gold)" />
                  <span className="text-xs text-muted-foreground">{amenity?.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Matterport Virtual Tours */}
      {matterportTours?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 bg-brand-gold/10 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="Scan" size={15} color="var(--color-brand-gold)" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground leading-tight">
                Visites virtuelles Matterport
              </h3>
              <p className="text-xs text-muted-foreground">
                Explorez l'infrastructure de {commune?.name} en 3D avant votre visite
              </p>
            </div>
          </div>

          {/* Active tour embed */}
          {activeTour && (
            <div className="mb-4 rounded-lg overflow-hidden border border-brand-gold/30 shadow-sm">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/60 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="Scan" size={13} color="var(--color-brand-gold)" />
                  <span className="text-xs font-medium text-foreground truncate max-w-[220px]">
                    {activeTour?.title}
                  </span>
                </div>
                <button
                  onClick={() => setActiveTour(null)}
                  className="text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded"
                  aria-label="Fermer la visite"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={activeTour?.url}
                  title={activeTour?.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  allow="xr-spatial-tracking"
                />
              </div>
              {activeTour?.description && (
                <div className="px-3 py-2 bg-muted/30 border-t border-border">
                  <p className="text-xs text-muted-foreground">{activeTour?.description}</p>
                </div>
              )}
            </div>
          )}

          {/* Tour cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {matterportTours?.map((tour, index) => {
              const isActive = activeTour?.id === (tour?.id || index);
              return (
                <button
                  key={tour?.id || index}
                  onClick={() => setActiveTour(isActive ? null : { ...tour, id: tour?.id || index })}
                  className={`group text-left rounded-lg border overflow-hidden transition-all duration-200 ${
                    isActive
                      ? 'border-brand-gold shadow-sm ring-1 ring-brand-gold/20'
                      : 'border-border hover:border-brand-gold/40 hover:shadow-sm'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden bg-muted">
                    {tour?.thumbnail ? (
                      <Image
                        src={tour?.thumbnail}
                        alt={tour?.title || `Visite virtuelle ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/60">
                        <Icon name="Scan" size={28} color="var(--color-brand-gold)" className="opacity-60" />
                      </div>
                    )}
                    {/* Play overlay */}
                    <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${isActive ? 'bg-brand-gold/20' : 'bg-black/30 group-hover:bg-black/20'}`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${isActive ? 'bg-brand-gold text-white' : 'bg-white/90 text-foreground group-hover:bg-brand-gold group-hover:text-white'}`}>
                        <Icon name={isActive ? 'Pause' : 'Play'} size={16} />
                      </div>
                    </div>
                    {/* Badge */}
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-black/60 text-white backdrop-blur-sm">
                        <Icon name="Scan" size={9} />
                        3D
                      </span>
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-2.5">
                    <p className={`text-xs font-medium line-clamp-1 transition-colors ${isActive ? 'text-brand-gold' : 'text-foreground group-hover:text-brand-gold'}`}>
                      {tour?.title || `Visite virtuelle ${index + 1}`}
                    </p>
                    {tour?.description && (
                      <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                        {tour?.description}
                      </p>
                    )}
                    <p className={`text-[11px] mt-1 font-medium transition-colors ${isActive ? 'text-brand-gold' : 'text-muted-foreground group-hover:text-brand-gold'}`}>
                      {isActive ? 'Fermer la visite ↑' : 'Lancer la visite →'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Properties in Commune */}
      {propertiesInCommune?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-base text-foreground">
              Autres propriétés à {commune?.name}
            </h3>
            <Link
              to={`/property-search-listings?commune=${commune?.id}`}
              className="text-xs font-medium text-brand-gold hover:text-brand-earth transition-colors"
            >
              Voir toutes →
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2]?.map(i => (
                <div key={i} className="animate-pulse rounded-lg border border-border overflow-hidden">
                  <div className="aspect-video bg-muted"></div>
                  <div className="p-3 space-y-2">
                    <div className="h-3.5 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {propertiesInCommune?.slice(0, 4)?.map((communeProperty) => (
                <Link
                  key={communeProperty?.id}
                  to={`/individual-property-page/${communeProperty?.id}`}
                  className="group block bg-card rounded-lg border border-border overflow-hidden hover:border-brand-gold/40 hover:shadow-sm transition-all duration-200"
                >
                  <div className="aspect-video overflow-hidden">
                    <Image
                      src={communeProperty?.images?.[0] || '/assets/images/no_image.png'}
                      alt={communeProperty?.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-foreground line-clamp-2 mb-2 group-hover:text-brand-gold transition-colors">
                      {communeProperty?.title}
                    </h4>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-brand-earth">
                        {formatPrice(communeProperty?.price)}
                      </span>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {communeProperty?.bedrooms && <span>{communeProperty?.bedrooms} ch</span>}
                        {communeProperty?.surface_area && (
                          <>
                            <span>·</span>
                            <span>{communeProperty?.surface_area}m²</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CTA */}
      <div className="p-4 bg-muted/40 rounded-md border border-border flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-foreground mb-0.5">
            Intéressé par ce quartier ?
          </p>
          <p className="text-xs text-muted-foreground">
            Découvrez toutes les propriétés disponibles à {commune?.name}
          </p>
        </div>
        <Button
          asChild
          variant="elegant"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
        >
          <Link to={`/commune-explorer?commune=${commune?.id}`}>Explorer</Link>
        </Button>
      </div>
    </div>
  );
};

export default CommuneContext;